import { ChatMessage } from '../../QnAWidget/types';
import { IEncryptionService, encryptionService } from './EncryptionService';

export interface IStorageService {
  saveMessages(messages: ChatMessage[]): Promise<void>;
  loadMessages(): Promise<ChatMessage[]>;
  saveContext(context: string): Promise<void>;
  loadContext(): Promise<string | null>;
  clearMessages(): void;
}

export class StorageService implements IStorageService {
  private readonly messagesStorageKey = 'qna-widget-messages';
  private readonly contextStorageKey = 'qna-widget-context';
  private readonly encryptionServiceInstance: IEncryptionService;

  constructor(encryptionServiceInstance: IEncryptionService) {
    this.encryptionServiceInstance = encryptionServiceInstance;
  }

  public async saveMessages(messages: ChatMessage[]): Promise<void> {
    try {
      const encryptedData = await this.encryptionServiceInstance.encrypt(JSON.stringify(messages));
      sessionStorage.setItem(this.messagesStorageKey, encryptedData);
    } catch (error) {
      // Silently handle storage errors
    }
  }

  public async loadMessages(): Promise<ChatMessage[]> {
    try {
      const stored = sessionStorage.getItem(this.messagesStorageKey);
      if (stored) {
        const decryptedData = await this.encryptionServiceInstance.decrypt(stored);
        const parsed = JSON.parse(decryptedData);
        return parsed.map((msg: unknown) => {
          const msgData = msg as Record<string, unknown>;
          return {
            ...msgData,
            timestamp: new Date(msgData.timestamp as string),
          } as ChatMessage;
        });
      }
    } catch (error) {
      // Silently handle storage errors
    }
    return [];
  }

  public async saveContext(context: string): Promise<void> {
    try {
      const encryptedData = await this.encryptionServiceInstance.encrypt(context);
      localStorage.setItem(this.contextStorageKey, encryptedData);
    } catch (error) {
      // Silently handle storage errors
    }
  }

  public async loadContext(): Promise<string | null> {
    try {
      const stored = localStorage.getItem(this.contextStorageKey);
      return stored ? await this.encryptionServiceInstance.decrypt(stored) : null;
    } catch (error) {
      // Silently handle storage errors
    }
    return null;
  }

  public clearMessages(): void {
    try {
      sessionStorage.removeItem(this.messagesStorageKey);
    } catch (error) {
      // Silently handle storage errors
    }
  }
}

export const storageService = new StorageService(encryptionService);
